import { useCallback, useEffect, useMemo, useState } from "react";
import { clientLocalCalendarDay } from "@/lib/clientLocalDay";
import {
  applyServiceCalendarEventFilters,
  buildScopeEvents,
  buildServiceWeekRollups,
  formatWeekRangeLabel,
  scopePanelTitle,
  weekDayKeys,
  type ServiceCalendarDayRollup,
  type ServiceCalendarListScope,
} from "@/lib/service/calendarLocalWeek";
import {
  getServiceEventsCalendar,
  listServiceEvents,
  listServiceRigs,
  listServiceTechs,
  listServiceWorkQueues,
  updateServiceEvent,
} from "@/lib/service/client";
import type { ServiceEvent, ServiceRig, ServiceTech, ServiceWorkQueue } from "@/lib/service/types";

function todayLocalCalendarDay(): string {
  return clientLocalCalendarDay();
}

export function useServiceCalendarPanel(navTick: number) {
  const [weekAnchor, setWeekAnchor] = useState(() => new Date());
  const [events, setEvents] = useState<ServiceEvent[]>([]);
  const [queues, setQueues] = useState<ServiceWorkQueue[]>([]);
  const [techs, setTechs] = useState<ServiceTech[]>([]);
  const [rigs, setRigs] = useState<ServiceRig[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(todayLocalCalendarDay());
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [queueFilter, setQueueFilter] = useState<string>("all");
  const [rigFilter, setRigFilter] = useState<string>("all");
  const [listScope, setListScope] = useState<ServiceCalendarListScope>("day");
  const [allOrgEvents, setAllOrgEvents] = useState<ServiceEvent[]>([]);

  const eventFilters = useMemo(
    () => ({ queueFilter, rigFilter }),
    [queueFilter, rigFilter]
  );

  const weekKeys = useMemo(() => weekDayKeys(weekAnchor), [weekAnchor]);
  const weekLabel = useMemo(() => formatWeekRangeLabel(weekKeys), [weekKeys]);
  const weekRollups = useMemo(
    () => buildServiceWeekRollups(weekKeys, events, eventFilters),
    [weekKeys, events, eventFilters]
  );

  const selectedDayRollup: ServiceCalendarDayRollup | null = useMemo(() => {
    if (!selectedDay) return null;
    return weekRollups.find((d) => d.date === selectedDay) ?? null;
  }, [weekRollups, selectedDay]);

  const schedulableEvents = useMemo(
    () =>
      applyServiceCalendarEventFilters(
        events.filter((e) => e.status === "unscheduled"),
        eventFilters
      ),
    [events, eventFilters]
  );

  const scopeEvents = useMemo(
    () =>
      buildScopeEvents(listScope, {
        selectedDayRollup,
        allOrgEvents,
        filters: eventFilters,
      }),
    [listScope, selectedDayRollup, allOrgEvents, eventFilters]
  );

  const scopeTitle = useMemo(
    () => scopePanelTitle(listScope, selectedDay, todayLocalCalendarDay()),
    [listScope, selectedDay]
  );

  const reloadReferenceData = useCallback(async () => {
    const [queuesRes, techsRes, rigsRes] = await Promise.all([
      listServiceWorkQueues({ active_only: true }),
      listServiceTechs({ active_only: true }),
      listServiceRigs({ active_only: true }),
    ]);
    setQueues(queuesRes.ok ? queuesRes.data.queues : []);
    setTechs(techsRes.ok ? techsRes.data.techs : []);
    setRigs(rigsRes.ok ? rigsRes.data.rigs : []);
  }, []);

  const reloadWeek = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    const keys = weekDayKeys(weekAnchor);
    const fromDate = keys[0]!;
    const toDate = keys[keys.length - 1]!;
    const queueOpts = queueFilter === "all" ? {} : { queue_id: queueFilter };
    const rigOpts = rigFilter === "all" ? {} : { rig_id: rigFilter };
    const listOpts = { ...queueOpts, ...rigOpts };

    const [calendarRes, unscheduledRes, allEventsRes] = await Promise.all([
      getServiceEventsCalendar({ scheduled_from: fromDate, scheduled_to: toDate }),
      listServiceEvents({ status: "unscheduled", ...listOpts }),
      listServiceEvents(listOpts),
    ]);
    setListBusy(false);

    if (!calendarRes.ok) {
      setEvents([]);
      setAllOrgEvents([]);
      setLocalError(calendarRes.error);
      return;
    }
    if (!unscheduledRes.ok) {
      setEvents([]);
      setAllOrgEvents([]);
      setLocalError(unscheduledRes.error);
      return;
    }
    if (!allEventsRes.ok) {
      setEvents([]);
      setAllOrgEvents([]);
      setLocalError(allEventsRes.error);
      return;
    }

    setAllOrgEvents(allEventsRes.data.events);

    const scheduled: ServiceEvent[] = [];
    for (const dayEvents of Object.values(calendarRes.data.days ?? {})) {
      for (const ev of dayEvents) {
        const queueOk = queueFilter === "all" || ev.queue_id === queueFilter;
        const rigOk =
          rigFilter === "all" ||
          ev.assigned_rig_id === rigFilter ||
          ev.required_rig_id === rigFilter;
        if (queueOk && rigOk) scheduled.push(ev);
      }
    }
    setEvents([...scheduled, ...unscheduledRes.data.events]);
  }, [weekAnchor, queueFilter, rigFilter]);

  useEffect(() => {
    void reloadReferenceData();
  }, [reloadReferenceData, navTick]);

  useEffect(() => {
    void reloadWeek();
  }, [reloadWeek, navTick]);

  const shiftWeek = useCallback((deltaWeeks: number) => {
    setWeekAnchor((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + deltaWeeks * 7);
      return next;
    });
    setSelectedEventId(null);
  }, []);

  const goToToday = useCallback(() => {
    setWeekAnchor(new Date());
    setSelectedDay(todayLocalCalendarDay());
    setSelectedEventId(null);
  }, []);

  const selectDay = useCallback((date: string) => {
    setSelectedDay((prev) => (prev === date ? null : date));
    setSelectedEventId(null);
  }, []);

  const selectEvent = useCallback((eventId: string) => {
    setSelectedEventId((prev) => (prev === eventId ? null : eventId));
  }, []);

  const changeListScope = useCallback((scope: ServiceCalendarListScope) => {
    setListScope(scope);
    setSelectedEventId(null);
    if (scope === "day" && !selectedDay) {
      setSelectedDay(todayLocalCalendarDay());
    }
  }, [selectedDay]);

  const refreshAll = useCallback(() => {
    void reloadReferenceData();
    void reloadWeek();
  }, [reloadReferenceData, reloadWeek]);

  const scheduleEvent = useCallback(
    async (payload: {
      event_id: string;
      scheduled_date: string;
      scheduled_window_label?: string;
      tech_id?: string | null;
      assigned_rig_id?: string | null;
    }) => {
      setActionBusy(true);
      setLocalError(null);
      const res = await updateServiceEvent(payload);
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return false;
      }
      await reloadWeek();
      return true;
    },
    [reloadWeek]
  );

  return {
    weekKeys,
    weekLabel,
    weekRollups,
    listBusy,
    actionBusy,
    localError,
    queues,
    techs,
    rigs,
    queueFilter,
    setQueueFilter,
    rigFilter,
    setRigFilter,
    listScope,
    setListScope: changeListScope,
    scopeEvents,
    scopeTitle,
    selectedDay,
    selectedDayRollup,
    selectedEventId,
    schedulableEvents,
    shiftWeek,
    goToToday,
    selectDay,
    selectEvent,
    refreshAll,
    scheduleEvent,
    reloadWeek,
  };
}
